import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { GetDataSourceResponse } from '../../../../models/data-source.interface';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { DataSourceService } from '../../../../core/services/data-source.service';
import { ConfirmationService, ConfirmEventType, MessageService } from 'primeng/api';

@Component({
  selector: 'globot-card-data-source',
  templateUrl: `card-data-source.component.html`,
  styleUrls: [`card-data-source.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardDataSourceComponent {
  @Input() dataSources: GetDataSourceResponse[] = [];
  @Input() botId: string = '';
  /**
   * GLOB-1895: hides the delete affordances for read-only roles. Defaults to
   * true so existing callers keep their behaviour.
   */
  @Input() canDelete = true;
  @Output() dataSourceDeleted = new EventEmitter<void>();

  classChip: string | undefined = undefined;

  constructor(
    private readonly dataSource: DataSourceService,
    private readonly ss: SpinnerService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cd: ChangeDetectorRef
  ) {}

  deleteDataSource(id: string) {
    // GLOB-1895: guard the handler, not just the template affordance.
    if (!this.canDelete) {
      return;
    }
    this.confirmationService.confirm({
      message: '¿Deseas eliminar esta fuente de datos? Esta acción no se puede revertir.',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ss.setHttpProgressStatus(true);
        this.dataSource.deleteDataSource(this.botId, id).subscribe(
          () => {
            this.ss.setHttpProgressStatus(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Acción exitosa',
              detail: '¡Fuente de datos eliminada!',
            });

            this.dataSources = this.dataSources
              .filter((dataSource) => dataSource != null)
              .map((dataSource) => {
                dataSource.children = dataSource.children.filter((child) => child.id !== id);
                return dataSource;
              })
              .filter((dataSource) => dataSource.id !== id);
            this.dataSourceDeleted.emit();
            this.cd.markForCheck();
          },
          () => {
            this.ss.setHttpProgressStatus(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Acción fallida',
              detail: 'La fuente de datos no pudo eliminarse',
            });
          }
        );
        this.confirmationService.close();
      },
      reject: (type: any) => {
        switch (type) {
          case ConfirmEventType.REJECT:
          case ConfirmEventType.CANCEL:
            this.messageService.add({
              severity: 'warn',
              summary: 'Acción cancelada',
              detail: 'Eliminar fuente de datos cancelado',
            });
            break;
        }
        this.confirmationService.close();
      },
    });
  }

  getType(type: string) {
    switch (type) {
      case 'url':
        return 'Sitio web';
      case 'pdf':
      case 'csv':
      case 'txt':
      case 'xls':
      case 'xlsx':
      case 'doc':
      case 'docx':
        return 'Archivo';
      default:
        return 'Desconocido';
    }
  }

  getStatus(status: string, isGroup: boolean = false) {
    let chipClass: string;
    let statusText: string;

    switch (status) {
      case 'COMPLETE':
        chipClass = 'chip chip-online';
        // El padre representa el job completo del sitio; las URLs hijas son "Conectado".
        statusText = isGroup ? 'Completado' : 'Conectado';
        break;
      case 'PENDING':
        chipClass = 'chip chip-pending';
        statusText = 'En espera';
        break;
      case 'FAILED':
      case 'ERROR':
        chipClass = 'chip chip-failed';
        statusText = 'Fallido';
        break;
      default:
        chipClass = 'chip';
        statusText = 'Desconocido';
    }

    return { chipClass, statusText }; // Devuelve ambos valores como un objeto
  }

  trackByDataSourceId(_index: number, dataSource: GetDataSourceResponse): string {
    return dataSource.id;
  }

  trackByChildId(_index: number, child: any): string {
    return child.id;
  }

  getIcon(type: string) {
    switch (type) {
      case 'url':
        return 'pi pi-globe';
      case 'pdf':
      case 'csv':
      case 'txt':
      case 'xls':
      case 'xlsx':
      case 'doc':
      case 'docx':
        return 'pi pi-file';
      default:
        return 'pi pi-file';
    }
  }
}
